<template>
  <div class="vmTableCell" @mouseover="hover = true" @mouseleave="hover = false">
    <div class="editValue">
      <div v-if="textEditor">
        <InputNumber
            v-if="mode === 'currency' || mode === 'decimal'"
            v-model="computedValue"
            :mode="mode"
            :currency="metaCurrency"
            :locale="metaLocale"
            :suffix="suffix"
            :min="getComputedMinMax ? getComputedMinMax.min : 0"
            :max="getComputedMinMax ? getComputedMinMax.max : 10000000"
            :min-fraction-digits="minPrecisionValue"
            :max-fraction-digits="precisionValue"
            :showButtons="elementEditorType !== 'text' && elementEditorType !== 'number'"
            :step="1"
            @keydown="keydown($event)"
            @click="checkValue($event)"
            :inputProps="{ autocomplete: 'off' }"
            class="vm-input"
            @mouseover="isHovering = true"
            @mouseout="isHovering = false"
            @focus="focus($event)"
            @blur="unfocus()"
        />
        <InputText
            v-else
            v-model="computedValue"
            :locale="metaLocale"
            :suffix="suffix"
            @click="checkValue"
            class="vm-input"
        />
        {{ suffix }}

      </div>
      <div v-else-if="checkboxEditor">
        <Checkbox v-model="checked" :binary="true" />
      </div>
      <div v-else @click="handleClick">
        {{ computedValueFormatted }}{{ suffix }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, nextTick, onMounted, PropType, ref} from "vue"
import useComputedTreeValue from "@/views/components/element/hooks/useComputedTreeValue"
import {TableCell, TableFilters} from "@/types/slides"
import {useGivingTreeStore, useSlidesStore, useWidgetStore} from "@/store"
import {getValuePropositionAPI, ValuePropositionAPI} from "@/services/value-proposition-api";
import {storeToRefs} from "pinia";

const givingTreeStore = useGivingTreeStore()

const {locale} = storeToRefs(useSlidesStore())
const {selectedWidget} = storeToRefs(useWidgetStore())

const {
  getActiveHookId,
  getComputedTextByHookId
} = useComputedTreeValue()


const props = defineProps({
  cellData: {
    type: Object,
    required: true
  },
  activeCell: {
    type: Boolean,
    required: true
  },
  filters: {
    type: Array as PropType<TableFilters[]>
  },
  tableCells: {
    type: Array as PropType<TableCell[][]>,
    required: true
  },
  cellRow: {
    type: Number,
    required: true
  },
  cellColumn: {
    type: Number,
    required: true
  }
})

const metaCurrency = computed(() => {
  return getComputedTextByHookId('meta-currency') ?? 'USD'
})
const metaLocale = computed(() => locale.value ?? 'en-US')
const elementEditorType = computed(() => selectedWidget.value?.editorType ?? '')


const emit = defineEmits<{
  (event: 'filteredValUpdate', payload: boolean): void
  (event: 'change', payload: TableCell[][]): void
}>()




const unfocus = () => {
  isFocused.value = false
  setTimeout(() => {
    if (!document.activeElement?.classList.contains('preserve-select-input')) {
      inputSelected.value = false
    }
  }, 100)
}


const widgetFormula = computed(() => {
  if(selectedWidgetValue.value) {
    return selectedWidgetValue.value.hasFormula
  }
  return false
})

const widgetEditable = computed(() => {
  if(selectedWidgetValue.value) {
    return !selectedWidgetValue.value.hasFormula && !selectedWidgetValue.value.readOnly
  }
  return false
})

const selectedWidgetValue = computed(() => {
  if (!props.cellData.hookId) {
    return
  }
  return givingTreeStore.getWidgetValue(props.cellData.hookId)
})


const handleClick = () => {
  if (widgetFormula.value) {
    showFormula()
    return
  }

  if (widgetEditable.value) {
    // TODO redo editor to be inline
    //dataEditVisible.value = !dataEditVisible.value
  }
}

const keydown = (event) => {

  const input = event.target;
  const isAllTextSelected = input.selectionStart === 0 && input.selectionEnd === input.value.length;

  if (event.key === 'ArrowLeft' && isAllTextSelected) {
    const hasCurrencySymbol = /^\p{Sc}/u.test(input.value);
    const cursorPosition = hasCurrencySymbol ? 1 : 0;
    input.setSelectionRange(cursorPosition, cursorPosition);
    event.stopPropagation();
  } else if (event.key === 'ArrowRight' && isAllTextSelected) {
    const cursorPosition = input.value.length;
    input.setSelectionRange(cursorPosition, cursorPosition + (event.key === 'ArrowRight' ? 1 : -1));
    event.stopPropagation();
  }

  if (event.key === '.' || event.key === ',') {
    if (isAllTextSelected) {
      input.value = 0; // This line might need adjustment depending on desired behavior
    }

    const cursorPosition = input.selectionStart;
    const nextInstancePosition = input.value.indexOf(event.key, cursorPosition);

    if (nextInstancePosition > -1) {
      const newCursorPosition = nextInstancePosition + 1;
      input.setSelectionRange(newCursorPosition, newCursorPosition);
    }
  }
};

const focus = (event) => {
  const input = event.target
  nextTick(() => {
    input.select()
  })


  isFocused.value = true
  inputSelected.value = true


}

const precisionValue = computed(() => {
  // precision is limited in display mode but not edit mode
  return isFocused.value ? 10 : getComputedPrecisionByHookId(props.cellData.hookId)
})
const minPrecisionValue = computed(() => {
  return getComputedPrecisionByHookId(props.cellData.hookId)
})

const { getComputedValue, getComputedMinMax, getComputedPrecision, getComputedPrecisionByHookId } = useComputedTreeValue()

const dataEditVisible = ref(false)
const isHovering = ref(false)
const isFocused = ref(false)
const {inputSelected} = storeToRefs(useWidgetStore())

const hover = ref(false)
const editable = computed(() => props.cellData.hookId && 'value' === (props.cellData.mapping ?? 'value'))
const textEditor = computed(() => props.cellData.hookId && 'text' === props.cellData.mappingEditor)
const checkboxEditor = computed(() => props.cellData.hookId && 'checkbox' === props.cellData.mappingEditor)

const inputBorder = computed(() => isFocused.value || isHovering.value ? '1px solid #DDD !important' : '1px solid transparent !important')
const inputBackground = computed(() => isFocused.value || isHovering.value ? 'white !important' : 'transparent !important')


const checked = computed(
    {
      get: () => {
        return givingTreeStore.getWidgetValue(props.cellData.hookId).value === 1
      },
      set: (val) => {
        givingTreeStore.setWidgetValue(props.cellData.hookId, val ? 1 : 0)
      }
    }
)

const mode = computed(() => {
  const value = givingTreeStore.getWidgetValue(props.cellData.hookId)

  if (value.type === 'meta') {
    return 'meta'
  }

  if (value.variableType === 'currency') {
    return 'currency'
  }
  return 'decimal'
})

const suffix = computed(() => {
  if (props.cellData.hookId) {
    const mapping = props.cellData.mapping ?? 'value'
    if (mapping !== 'value') {
      return
    }
    const value = givingTreeStore.getWidgetValue(props.cellData.hookId)
    if (value && value.variableType === 'percent') {
      return '%'
    }
  }
})

const computedValueFormatted = computed(() => {
  if (!props.cellData.hookId) {
    return null
  }

  const mapping = props.cellData.mapping ?? 'value'
  const value = givingTreeStore.getWidgetValue(props.cellData.hookId)
  if (!value) { return '' }

  if (mapping === 'value') {

    if (value.type === 'meta') {
      return value.text
    }

    if (value.isChoice) {
      return value.selectedChoiceName
    }

    if (value.value === undefined || value.value === null) {
      props.cellData.mapping = "name"
      return value.name
    }

    if (value.variableType === 'currency') {
      return currencyFormat(value.value, parseInt(value.precision ?? 0))
    }
    else if (value.precision) {
      return numberFormat(value.value, parseInt(value.precision ?? 0))
    }
  }

  return value[mapping]
})

const computedValue = computed({
  get() {
    if (!props.cellData.hookId) {
      return null
    }

    const value = givingTreeStore.getWidgetValue(props.cellData.hookId)
    if (value.type === 'meta') {
      return value.text
    }

    return value.value
  },
  set(val) {
    givingTreeStore.setWidgetValue(props.cellData.hookId, val)
  }
})

const showFormula = async () => {

  const api: ValuePropositionAPI | null = getValuePropositionAPI()

  const widget = givingTreeStore.getWidgetValue(props.cellData.hookId)

  if (widget.vpHasFormula) api?.fireUIEvent("varHandler", {action: "show_var", variableUid: widget.uid});



}

const checkValue = (event) => {

  const existing = existingVarFilter()
  if (existing.length > 0) emit('filteredValUpdate', true)

  const input = event.target
  if (input) {
    input.select()
  }

}

const existingVarFilter = () => {
  if (!props.filters) return []

  return props.filters.filter(item => {
    const parsedValues = JSON.parse(item.values)
    return Object.values(parsedValues).includes(props.cellData.hookId)
  })
}




const currencyFormat = (value, precision) => {
  const hasCompactElementData = props.cellData.compact || false

  const notation = value >= 100000 && hasCompactElementData ? 'compact' : 'standard'
  return new Intl.NumberFormat(metaLocale.value, {
    notation: notation,
    compactDisplay: "long",
    minimumFractionDigits: minPrecisionValue.value,
    maximumFractionDigits: minPrecisionValue.value,
    style: mode.value,
    currency: metaCurrency.value
  }).format(value)
}

const numberFormat = (value, precision) => {
  const hasCompactElementData = props.cellData.compact

  const notation = value >= 100000 && hasCompactElementData ? 'compact' : 'standard'
  return new Intl.NumberFormat(metaLocale.value, {
    notation: notation,
    compactDisplay: "long",
    minimumFractionDigits: minPrecisionValue.value,
    maximumFractionDigits: minPrecisionValue.value,
    style: mode.value
  }).format(value)
}
</script>

<style lang="scss" scoped>
.vmTableCell {
  display: flex;
  flex-direction: column;
  position: relative;
}

.editBtn {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  height: 100%;
}

.vmTableCell:hover .editBtn {
  display: inline;
  padding-left: 10px;
}

.editValue {
  display: flex;
}

.p-inputnumber.vm-input > .p-inputnumber-input {
  width: 200px !important;
}

.cell-text {
  cursor: text;
}

:deep(.p-inputnumber .p-inputtext) {
  background-color: v-bind(inputBackground);
  border: v-bind(inputBorder);
}
</style>
